import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Box,
  MenuItem,
} from "@mui/material";
import { useSnackbar } from "components/GlobalSnackbar";
import { apiGetAllZones } from "api/zones";
import { apiGetAllHarta } from "api/harta";
import { apiTambahPenyewa } from "api/penyewa";
import { apiCheckKodHartaAvailability } from "api/akaun";
import { getCurrentUserDepartment } from "utils/userUtils";
import { apiGetCustomerByNokp } from "api/customers-paginated";
import { autoCapitalizePayload } from "utils/autoCapitalize";
import { formatIcNumber, formatPhoneNumber } from "utils/inputFormatters";
import { buttonStyles } from "utils/buttonStyles";
import { dialogHeaderStyles, dialogContentStyles } from "utils/dialogStyles";

function TambahPenyewa({ open, onClose, onSave }) {
  const { showError } = useSnackbar();
  const [formState, setFormState] = useState({
    // Maklumat Nama
    nama: "",
    no_kp: "",
    no_tel: "",
    emel: "",
    alamat1_pelanggan: "",
    alamat2_pelanggan: "",
    poskod_pelanggan: "",
    bandar_pelanggan: "",
    negeri_pelanggan: "",
    // Maklumat Syarikat
    no_ssm: "",
    nama_syarikat: "",
    // Maklumat Waris
    nama_waris: "",
    telefon_waris: "",
    hubungan_waris: "",
    alamat_waris: "",
    // Maklumat Akaun Bank
    no_akaun_bank: "",
    no_akaun_bank_syarikat: "",
    nama_bank: "",
    // Akaun/Harta fields
    kod_pelanggan: "",
    kod_harta: "",
    zon: "",
    alamat1_harta: "",
    alamat2_harta: "",
    poskod_harta: "",
    bandar_harta: "",
    negeri_harta: "",
    kadar_sewa: "",
    kategori_kutipan: "",
    bahagian_pemantau: "",
    catatan: "",
    tarikh_mula: "",
    tarikh_tamat: "",
  });

  // State for zones dropdown
  const [zones, setZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  // State for harta dropdown
  const [hartaList, setHartaList] = useState([]);
  const [loadingHarta, setLoadingHarta] = useState(true);
  // State for disabling Simpan button
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;
    
    // Auto-format IC number
    if (name === 'no_kp') {
      formattedValue = formatIcNumber(value);
    }
    // Auto-format phone numbers
    else if (name === 'no_tel' || name === 'telefon_waris') {
      formattedValue = formatPhoneNumber(value);
    }
    
    setFormState({ ...formState, [name]: formattedValue });
  };

  const handleHartaChange = (e) => {
    const selectedKodHarta = e.target.value;
    const selectedHarta = hartaList.find((harta) => harta.kod_harta === selectedKodHarta);
    if (selectedHarta) {
      setFormState({
        ...formState,
        kod_harta: selectedKodHarta,
        zon: selectedHarta.zon || "",
        alamat1_harta: selectedHarta.alamat1_harta || "",
        alamat2_harta: selectedHarta.alamat2_harta || "",
        poskod_harta: selectedHarta.poskod_harta || "",
        bandar_harta: selectedHarta.bandar_harta || "",
        negeri_harta: selectedHarta.negeri_harta || "",
        kadar_sewa: selectedHarta.kadar_sewa || "",
        kategori_kutipan: selectedHarta.kategori_kutipan || "",
      });
    } else {
      setFormState({ ...formState, kod_harta: selectedKodHarta });
    }
  };

  const handleSave = async () => {
    // Only validate and pass data up to parent
    if (saving) return;
    setSaving(true);
    // Validate required fields
    const requiredFields = [
      { field: "no_kp", label: "No KP" },
      { field: "kod_harta", label: "Kod Harta" },
      { field: "tarikh_mula", label: "Tarikh Mula" },
      { field: "tarikh_tamat", label: "Tarikh Tamat" },
      { field: "bahagian_pemantau", label: "Bahagian Pemantau" },
    ];
    const emptyFields = requiredFields.filter(({ field }) => !(formState[field] || "").trim());
    if (emptyFields.length > 0) {
      alert(`Sila lengkapkan medan yang diperlukan: ${emptyFields.map((f) => f.label).join(", ")}`);
      setSaving(false);
      return;
    }
    if (!(formState.kod_pelanggan || "").trim()) {
      alert("Kod Pelanggan wajib diisi.");
      setSaving(false);
      return;
    }
    let bahagianPemantauToSend = formState.bahagian_pemantau;
    if (!(bahagianPemantauToSend || "").trim()) {
      bahagianPemantauToSend = getCurrentUserDepartment() || "";
    }
    if (!(bahagianPemantauToSend || "").trim()) {
      alert("Bahagian Pemantau wajib diisi.");
      setSaving(false);
      return;
    }
    // Helper to format date to ISO-8601 (yyyy-MM-dd) or return null if invalid
    const toISODate = (val) => {
      if (!val) return ""; // Return empty string instead of null
      if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
      const d = new Date(val);
      if (isNaN(d)) return "";
      return d.toISOString().slice(0, 10);
    };
    // Prepare nested payload for parent
    const akaunData = {
      nama: formState.nama,
      no_kp: (formState.no_kp || "").trim(),
      no_tel: formState.no_tel,
      emel: formState.emel,
      kod_pelanggan: (formState.kod_pelanggan || "").trim(),
      no_ssm: formState.no_ssm,
      nama_syarikat: formState.nama_syarikat,
      kod_harta: (formState.kod_harta || "").trim(),
      zon: formState.zon,
      alamat1_harta: formState.alamat1_harta,
      alamat2_harta: formState.alamat2_harta,
      poskod_harta: formState.poskod_harta,
      bandar_harta: formState.bandar_harta,
      negeri_harta: formState.negeri_harta,
      kadar_sewa: formState.kadar_sewa,
      kategori_kutipan: formState.kategori_kutipan,
      bahagian_pemantau: bahagianPemantauToSend,
      catatan: formState.catatan,
      tarikh_mula: toISODate(formState.tarikh_mula),
      tarikh_tamat: toISODate(formState.tarikh_tamat),
      status_akaun: "Aktif",
      id_nama: "",
      no_akaun_lama: "",
      kod_kategori: "",
      bayaran_semasa: 0.0,
      baki_akhir_lejar: 0.0,
      baki_akhir_lejar_asal: 0.0,
      peratus_diskaun: 0.0,
      perkara_akaun_pbd: "",
      amaun_bayaran_pbd: 0.0,
      tmp_mark_ht: 0,
      status_jana: 0,
    };
    const namaData = {
      nama: formState.nama,
      no_kp: (formState.no_kp || "").trim(),
      no_tel: formState.no_tel,
      emel: formState.emel,
      alamat1_pelanggan: formState.alamat1_pelanggan,
      alamat2_pelanggan: formState.alamat2_pelanggan,
      poskod_pelanggan: formState.poskod_pelanggan,
      bandar_pelanggan: formState.bandar_pelanggan,
      negeri_pelanggan: formState.negeri_pelanggan,
      no_ssm: formState.no_ssm,
      nama_syarikat: formState.nama_syarikat,
      nama_waris: formState.nama_waris,
      telefon_waris: formState.telefon_waris,
      hubungan_waris: formState.hubungan_waris,
      alamat_waris: formState.alamat_waris,
      no_akaun_bank: formState.no_akaun_bank,
      no_akaun_bank_syarikat: formState.no_akaun_bank_syarikat,
      bahagian_pemantau: (bahagianPemantauToSend || "").trim(),
      zon: formState.zon,
      nama_bank: "",
    };
    const payload = {
      kod_harta: (formState.kod_harta || "").trim(),
      no_kp: (formState.no_kp || "").trim(),
      akaunData,
      namaData,
      bahagian_pemantau: (bahagianPemantauToSend || "").trim(),
    };
    if (onSave) onSave(payload);
    setSaving(false);
  };

  // Fetch zones and harta on component mount
  useEffect(() => {
    const fetchZones = async () => {
      try {
        setLoadingZones(true);
        const response = await apiGetAllZones(1, 100); // Get first 100 zones
        if (response.data) {
          setZones(response.data);
        }
      } catch (error) {
        showError("Gagal memuatkan zon.");
        setZones([]);
      } finally {
        setLoadingZones(false);
      }
    };

    const fetchHarta = async () => {
      try {
        setLoadingHarta(true);
        // Get harta for current user's department (department filtering is handled in API headers)
        const response = await apiGetAllHarta(1, 1000); // Get first 1000 harta records
        if (response.data) {
          setHartaList(response.data);
        }
      } catch (error) {
        showError("Gagal memuatkan harta.");
        setHartaList([]);
      } finally {
        setLoadingHarta(false);
      }
    };

    const autoFillUserDepartment = () => {
      const userDepartment = getCurrentUserDepartment();
      setFormState((prevState) => ({
        ...prevState,
        bahagian_pemantau: userDepartment,
      }));
    };

    if (open) {
      fetchZones();
      fetchHarta();
      autoFillUserDepartment();
    }
  }, [open]);

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={dialogHeaderStyles.profile}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" sx={{ color: "#fff" }}>Tambah Penyewa</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ ...dialogContentStyles.light, p: 1.2 }}>
        {/* Maklumat Pelanggan */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
          Maklumat Pelanggan
        </Typography>
        <Divider sx={{ mb: 1.2 }} />
        <Grid container spacing={1.2}>
          <Grid item xs={12}>
            <TextField
              label="👤 Nama"
              name="nama"
              value={formState.nama}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="🆔 No KP"
              name="no_kp"
              value={formState.no_kp}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="📱 No Tel"
              name="no_tel"
              value={formState.no_tel}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="✉️ Emel"
              name="emel"
              value={formState.emel}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏠 Alamat 1 Pelanggan"
              name="alamat1_pelanggan"
              value={formState.alamat1_pelanggan}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏠 Alamat 2 Pelanggan"
              name="alamat2_pelanggan"
              value={formState.alamat2_pelanggan}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="📮 Poskod Pelanggan"
              name="poskod_pelanggan"
              value={formState.poskod_pelanggan}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="🏙️ Bandar Pelanggan"
              name="bandar_pelanggan"
              value={formState.bandar_pelanggan}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="🗺️ Negeri Pelanggan"
              name="negeri_pelanggan"
              value={formState.negeri_pelanggan}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🆔 Kod Pelanggan (Dari SAP)"
              name="kod_pelanggan"
              value={formState.kod_pelanggan}
              onChange={handleChange}
              required
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          
        </Grid>

        {/* Maklumat Syarikat */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }}>
          Maklumat Syarikat
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="🏢 No SSM"
              name="no_ssm"
              value={formState.no_ssm}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="🏢 Nama Syarikat"
              name="nama_syarikat"
              value={formState.nama_syarikat}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
        </Grid>

        {/* Maklumat Waris */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }}>
          Maklumat Waris
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.2}>
          <Grid item xs={12} md={4}>
            <TextField
              label="👤 Nama Waris"
              name="nama_waris"
              value={formState.nama_waris}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="📱 No Telefon Waris"
              name="telefon_waris"
              value={formState.telefon_waris}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="🤝 Hubungan Waris"
              name="hubungan_waris"
              value={formState.hubungan_waris}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <TextField
              label="🏠 Alamat Waris"
              name="alamat_waris"
              value={formState.alamat_waris}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
        </Grid>
        {/* Maklumat Akaun Bank */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3 }}>
          Maklumat Akaun Bank
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={1.2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏦 Akaun Bank Perseorangan"
              name="no_akaun_bank"
              value={formState.no_akaun_bank || ""}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏦 Akaun Bank Syarikat"
              name="no_akaun_bank_syarikat"
              value={formState.no_akaun_bank_syarikat || ""}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏦 Nama Bank"
              name="nama_bank"
              value={formState.nama_bank || ""}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Contoh: Maybank, CIMB, RHB"
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
        </Grid>

        {/* Separator Line */}
        <Divider sx={{ my: 3, borderColor: "#2196f3" }} />

        {/* Maklumat Harta Sewaan */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
          Maklumat Harta Sewaan
        </Typography>
        <Divider sx={{ mb: 1.2 }} />
        <Grid container spacing={1}>
          {/* Kod Harta - moved to top as dropdown */}
          <Grid item xs={12} md={6}>
            <FormControl
              fullWidth
              size="small"
              required
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#fff",
                  fontSize: "0.875rem",
                  p: 1.2,
                  "&:hover fieldset": { borderColor: "#2196f3" },
                  "&.Mui-focused fieldset": { borderColor: "#2196f3" },
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.875rem",
                },
              }}
            >
              <InputLabel>🏷️ Kod Harta</InputLabel>
              <Select
                label="🏷️ Kod Harta"
                name="kod_harta"
                value={formState.kod_harta}
                onChange={handleHartaChange}
                fullWidth
              >
                <MenuItem value="">
                  <em>Pilih Kod Harta</em>
                </MenuItem>
                {loadingHarta ? (
                  <MenuItem value="" disabled>
                    Loading...
                  </MenuItem>
                ) : (
                  hartaList.map((harta) => (
                    <MenuItem key={harta.id} value={harta.kod_harta}>
                      {harta.kod_harta} - {harta.alamat1_harta}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Zon Harta as a text field for debugging autofill */}
          <Grid item xs={12} md={6}>
            <TextField
              label="🎯 Zon Harta"
              name="zon"
              value={formState.zon}
              onChange={handleChange}
              fullWidth
              size="small"
              variant="outlined"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>

          {/* Address fields in varied layout */}
          <Grid item xs={12}>
            <TextField
              label="📍 Alamat 1 Harta"
              name="alamat1_harta"
              value={formState.alamat1_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="📍 Alamat 2 Harta"
              name="alamat2_harta"
              value={formState.alamat2_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="📮 Poskod Harta"
              name="poskod_harta"
              value={formState.poskod_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏙️ Bandar Harta"
              name="bandar_harta"
              value={formState.bandar_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🗺️ Negeri Harta"
              name="negeri_harta"
              value={formState.negeri_harta}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField
              label="💵 Kadar Sewa (RM)"
              name="kadar_sewa"
              value={formState.kadar_sewa}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              type="number"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="📂 Kategori Kutipan"
              name="kategori_kutipan"
              value={formState.kategori_kutipan}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="🏢 Bahagian Pemantau"
              name="bahagian_pemantau"
              value={formState.bahagian_pemantau}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                style: { backgroundColor: "#f9f9f9ff", fontSize: "0.85rem", padding: 3 },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="📅 Tarikh Mula"
              name="tarikh_mula"
              value={formState.tarikh_mula}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              type="date"
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                style: {
                  backgroundColor: "#ffffffff",
                  fontSize: "0.85rem",
                  padding: 3,
                },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="📅 Tarikh Tamat"
              name="tarikh_tamat"
              value={formState.tarikh_tamat}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              type="date"
              size="small"
              required
              InputLabelProps={{ shrink: true }}
              InputProps={{
                style: {
                  backgroundColor: "#ffffffff",
                  fontSize: "0.85rem",
                  padding: 3,
                },
              }}
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
        </Grid>

        {/* Notes field - full width */}
        <Divider sx={{ my: 3, borderColor: "#9c27b0" }} />
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <TextField
              label="📝 Catatan"
              name="catatan"
              value={formState.catatan}
              onChange={handleChange}
              fullWidth
              variant="outlined"
              multiline
              rows={2}
              size="small"
              placeholder="Masukkan catatan tambahan di sini..."
              sx={{
                fontSize: "0.85rem",
                "& .MuiOutlinedInput-root": { fontSize: "0.85rem", p: 0.3 },
                "& .MuiInputLabel-root": { fontSize: "0.85rem" },
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ ...dialogContentStyles.light, p: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          size="small"
          disabled={saving}
          sx={{
            ...buttonStyles.save,
            m: 0,
            px: 2,
            minWidth: 0
          }}
        >
          {saving ? "MENYIMPAN..." : "SIMPAN"}
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          size="small"
          sx={{
            ...buttonStyles.cancel,
            m: 0,
            px: 2,
            minWidth: 0
          }}
        >
          BATAL
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

TambahPenyewa.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default TambahPenyewa;


// Snackbar UI for feedback
// Render outside the Dialog to avoid clip

